import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import SkeletonList from '../../../../../components/MUIComponents/SkeletonList';
import {
  ISelectedCity,
  ISelectedCountry,
} from '../../../../../interfaces/country.interface';
import { CityOption } from '../../../../../utils/coutry-city-utils';
import CheckBox from './components/CheckBox';

interface ICandidateCityFilterProps {
  cities: CityOption[];
  label: string;
  onShowMore: () => void;
  totalCityCount: number;
  handleCityChange: (data: ISelectedCity) => void;
  handleSearchChange: (data: string) => void;
  resetTrigger: boolean;
  selectedCities: ISelectedCity[];
  isLoading: boolean;
  isError: boolean;
  selectedCountries: ISelectedCountry[];
}

const CandidateCityFilter = ({
  cities,
  label: _label,
  onShowMore,
  totalCityCount,
  handleCityChange,
  handleSearchChange,
  resetTrigger,
  selectedCities,
  isLoading,
  isError,
  selectedCountries,
}: ICandidateCityFilterProps) => {
  const [searchText, setSearchText] = useState('');
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    setSearchText('');
    setAllSelected(false);
  }, [resetTrigger]);

  const handleAllCitiesChange = () => {
    if (allSelected) {
      selectedCities.forEach((city) => handleCityChange(city));
    } else {
      selectedCities.forEach((city) => handleCityChange(city));
      cities.forEach((city) =>
        handleCityChange({ id: city.id, name: city.name })
      );
    }
    setAllSelected(!allSelected);
  };

  const handleSingleCityChange = (city: ISelectedCity) => {
    if (allSelected) {
      selectedCities.forEach((city) => handleCityChange(city));
      setAllSelected(false);
    }
    handleCityChange(city);
  };

  if (isLoading) {
    return (
      <Box
        sx={{ p: '22px 16px 11px 16px' }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <SkeletonList />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography sx={{ p: '22px 16px 11px 16px' }} color="error" variant="h4">
        Failed to get cities
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.375rem',
        padding: '0 1rem 1.375rem',
      }}
    >
      <Typography variant="badge2" marginTop={'0.7rem'}>
        City
      </Typography>

      <TextField
        variant="outlined"
        placeholder="Search..."
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          handleSearchChange(e.target.value.trim());
        }}
        sx={searchFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ p: 0 }}>
              <SearchIcon sx={iconSizes} />
            </InputAdornment>
          ),
        }}
      />

      {selectedCountries.length === 0 && (
        <Typography
          sx={{ p: '22px 16px 11px 16px' }}
          color="error"
          variant="h4"
        >
          Please select a country first.
        </Typography>
      )}
      {selectedCountries.length > 0 && cities.length === 0 && (
        <Typography
          sx={{ p: '22px 16px 11px 16px' }}
          color="error"
          variant="h4"
        >
          No cities available for the selected country.
        </Typography>
      )}
      {selectedCountries.length > 0 && cities.length > 0 && (
        <>
          <FormGroup>
            {/* Default "All" Checkbox */}
            <CheckBox
              label={'All'}
              checked={allSelected}
              onChange={handleAllCitiesChange}
            />
            {/* List of Cities */}
            {cities?.map((city) => (
              <CheckBox
                key={city.id}
                label={city.name}
                checked={
                  !!selectedCities.find(
                    (selectedCitie) => selectedCitie.id === city.id
                  )
                }
                onChange={() =>
                  handleSingleCityChange({ id: city.id, name: city.name })
                }
              />
            ))}
          </FormGroup>

          {cities && cities.length < totalCityCount && (
            <Typography
              onClick={onShowMore}
              variant="h4"
              component="h4"
              color={'text.accent'}
              sx={{ marginTop: '10px', cursor: 'pointer' }}
            >
              + Show more
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default CandidateCityFilter;

const searchFieldStyles = {
  width: '100%',
  maxWidth: { md: '16rem', lg: '20.125rem' },
  '& .MuiInputBase-root': { height: '3.125rem', padding: '0.75rem 1rem' },
  '& .MuiOutlinedInput-input': { height: '3.125rem !important', padding: 0 },
  '& .MuiSvgIcon-root': { backgroundColor: 'transparent' },
};

const iconSizes = { width: '1.25rem', height: '1.25rem' };
